import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';

type NewsCategory = '공지사항' | '행사안내' | '교우소식';

const NEWS_CATEGORIES: NewsCategory[] = ['공지사항', '행사안내', '교우소식'];
const NEWS_FILTERS: Array<'전체' | NewsCategory> = ['전체', ...NEWS_CATEGORIES];

type NewsRow = {
  id: number;
  category: NewsCategory;
  title: string;
  date: string;
  place: string;
  summary: string;
  thumbIcon: string;
  content: string;
  eventInfo: string;
  note: string;
  verseQuote: string;
  verseRef: string;
};

type NewsForm = {
  category: NewsCategory;
  title: string;
  date: string;
  place: string;
  summary: string;
  thumbIcon: string;
  content: string;
  eventInfo: string;
  note: string;
  verseQuote: string;
  verseRef: string;
};

const EMPTY_FORM: NewsForm = {
  category: '공지사항',
  title: '',
  date: '',
  place: '',
  summary: '',
  thumbIcon: '',
  content: '',
  eventInfo: '',
  note: '',
  verseQuote: '',
  verseRef: '',
};

function toDateInputValue(displayDate: string) {
  const match = displayDate.match(/^(\d{4})\.(\d{2})\.(\d{2})$/);
  if (!match) return '';
  return `${match[1]}-${match[2]}-${match[3]}`;
}

const CATEGORY_BADGE: Record<NewsCategory, string> = {
  공지사항: 'manage-badge--blue',
  행사안내: 'manage-badge--purple',
  교우소식: 'manage-badge--green',
};

export default function ManageNews({ authChurch }: { authChurch: string }) {
  const [rows, setRows] = useState<NewsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<'전체' | NewsCategory>('전체');
  const [search, setSearch] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<NewsForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchNews = () => {
    setLoading(true);
    setError(null);
    axios
      .get(`${MainURL}/news`, { params: { authChurch } })
      .then((res) => setRows(res.data?.data ?? []))
      .catch((err) => {
        console.error('GET /news', err);
        setError('교회소식을 불러오지 못했습니다.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchNews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChurch]);

  const openCreateForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEditForm = (row: NewsRow) => {
    setEditingId(row.id);
    setForm({
      category: row.category,
      title: row.title,
      date: toDateInputValue(row.date),
      place: row.place,
      summary: row.summary,
      thumbIcon: row.thumbIcon,
      content: row.content,
      eventInfo: row.eventInfo,
      note: row.note,
      verseQuote: row.verseQuote,
      verseRef: row.verseRef,
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const submitForm = () => {
    if (!form.title.trim() || !form.date) return;
    setSaving(true);
    const request = editingId
      ? axios.put(`${MainURL}/news/${editingId}`, { authChurch, ...form })
      : axios.post(`${MainURL}/news`, { authChurch, ...form });

    request
      .then(() => {
        closeForm();
        fetchNews();
      })
      .catch((err) => console.error(editingId ? 'PUT /news/:id' : 'POST /news', err))
      .finally(() => setSaving(false));
  };

  const removeNews = (id: number) => {
    if (!window.confirm('이 소식을 삭제할까요?')) return;
    axios
      .delete(`${MainURL}/news/${id}`, { params: { authChurch } })
      .then(() => fetchNews())
      .catch((err) => console.error('DELETE /news/:id', err));
  };

  const categoryFilteredRows = activeFilter === '전체' ? rows : rows.filter((row) => row.category === activeFilter);

  const filteredRows = useMemo(() => {
    const keyword = search.trim();
    if (!keyword) return categoryFilteredRows;
    return categoryFilteredRows.filter(
      (row) => row.title.includes(keyword) || row.summary.includes(keyword),
    );
  }, [categoryFilteredRows, search]);

  const filterCounts = useMemo(() => {
    const counts: Record<'전체' | NewsCategory, number> = {
      전체: rows.length,
      공지사항: 0,
      행사안내: 0,
      교우소식: 0,
    };
    rows.forEach((row) => {
      counts[row.category] += 1;
    });
    return counts;
  }, [rows]);

  return (
    <div className="manage-panel">
      <div className="manage-panel-header">
        <div>
          <h2 className="manage-panel-title">교회소식</h2>
          <p className="manage-panel-desc">앱에 게시되는 공지·행사·교우 소식을 관리합니다.</p>
        </div>
        <button type="button" className="manage-btn manage-btn--primary" onClick={openCreateForm}>
          + 새 소식 등록
        </button>
      </div>

      {loading && <div className="manage-state">불러오는 중입니다...</div>}
      {!loading && error && <div className="manage-state manage-state--error">{error}</div>}

      {!loading && !error && (
        <>
          <div className="manage-stats">
            <div className="manage-stat-card">
              <span>전체 소식</span>
              <strong>{filterCounts['전체']}<small> 건</small></strong>
            </div>
            <div className="manage-stat-card">
              <span>공지사항</span>
              <strong>{filterCounts['공지사항']}<small> 건</small></strong>
            </div>
            <div className="manage-stat-card">
              <span>행사안내</span>
              <strong>{filterCounts['행사안내']}<small> 건</small></strong>
            </div>
            <div className="manage-stat-card">
              <span>교우소식</span>
              <strong>{filterCounts['교우소식']}<small> 건</small></strong>
            </div>
          </div>

          <div className="manage-filterbar">
            <div className="manage-seg">
              {NEWS_FILTERS.map((filter) => (
                <button
                  key={filter}
                  type="button"
                  className={`manage-seg-btn${activeFilter === filter ? ' is-active' : ''}`}
                  onClick={() => setActiveFilter(filter)}
                >
                  {filter} ({filterCounts[filter]})
                </button>
              ))}
            </div>
            <div className="manage-filterbar-search">
              <input
                placeholder="제목, 내용으로 검색"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="manage-grid-2col">
            <section className="manage-card-block">
              <div className="manage-block-head">
                <h3 className="manage-block-title">소식 목록</h3>
                <span className="manage-block-count">{filteredRows.length}</span>
              </div>
              <div className="manage-news-list">
                {filteredRows.length === 0 && (
                  <div className="manage-table-empty">등록된 소식이 없습니다.</div>
                )}
                {filteredRows.map((row) => (
                  <div className="manage-news-card" key={row.id}>
                    <div className="manage-news-card-row">
                      <div className="manage-news-card-body">
                        <span className={`manage-news-label manage-badge ${CATEGORY_BADGE[row.category]}`}>{row.category}</span>
                        <p className="manage-news-title">{row.title}</p>
                        <p className="manage-news-date">{row.date}{row.place ? ` · ${row.place}` : ''}</p>
                        <p className="manage-news-summary">{row.summary}</p>
                      </div>
                      <div className="manage-news-thumb">
                        <span aria-hidden>{row.thumbIcon}</span>
                      </div>
                    </div>
                    <div className="manage-news-card-actions">
                      <div className="manage-row-actions">
                        <button type="button" className="manage-btn manage-btn--sm" onClick={() => openEditForm(row)}>수정</button>
                        <button type="button" className="manage-btn manage-btn--sm manage-btn--danger" onClick={() => removeNews(row.id)}>삭제</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <aside className="manage-side-stack">
              <div className="manage-side-card manage-worship-phone">
                <div className="manage-worship-phone-notch">
                  교회소식
                  <small>앱 소식 탭 미리보기</small>
                </div>
                <div className="manage-worship-phone-screen">
                  {filteredRows.length === 0 && (
                    <div className="manage-side-empty">등록된 소식이 없습니다.</div>
                  )}
                  {filteredRows.slice(0, 5).map((row) => (
                    <div className="manage-worship-prow" key={row.id}>
                      <b>{row.title}</b>
                      <span>{row.date}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="manage-side-card">
                <div className="manage-side-card-head">운영 팁</div>
                <div className="manage-side-card-body manage-tip-list">
                  <div className="manage-tip">
                    <span className="manage-tip-dot" />
                    <span>분류는 <b>공지사항·행사안내·교우소식</b> 중에서 선택하며, 필터바에서 구분해 볼 수 있습니다.</span>
                  </div>
                  <div className="manage-tip">
                    <span className="manage-tip-dot" />
                    <span>아이콘 칸에 이모지 1자를 입력하면 목록 썸네일과 앱 미리보기에 그대로 표시됩니다.</span>
                  </div>
                  <div className="manage-tip">
                    <span className="manage-tip-dot" />
                    <span>요약은 목록에 노출되고, 본문·행사 정보·비고·말씀은 등록 시 함께 저장됩니다.</span>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </>
      )}

      {formOpen && (
        <div className="manage-modal-overlay" onClick={closeForm}>
          <div className="manage-modal" onClick={(e) => e.stopPropagation()}>
            <div className="manage-modal-header">
              <h3>{editingId ? '소식 수정' : '새 소식 등록'}</h3>
              <button type="button" className="manage-modal-close" onClick={closeForm}>×</button>
            </div>
            <div className="manage-modal-body">
              <div className="manage-form manage-form--grid">
                <label className="manage-field">
                  <span>분류</span>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as NewsCategory })}>
                    {NEWS_CATEGORIES.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </label>
                <label className="manage-field">
                  <span>날짜</span>
                  <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                </label>
                <label className="manage-field manage-field--full">
                  <span>제목</span>
                  <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </label>
                <label className="manage-field">
                  <span>장소</span>
                  <input value={form.place} onChange={(e) => setForm({ ...form, place: e.target.value })} />
                </label>
                <label className="manage-field">
                  <span>아이콘</span>
                  <input value={form.thumbIcon} onChange={(e) => setForm({ ...form, thumbIcon: e.target.value })} placeholder="예: ✝" />
                </label>
                <label className="manage-field manage-field--full">
                  <span>요약</span>
                  <textarea rows={2} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
                </label>
                <label className="manage-field manage-field--full">
                  <span>본문</span>
                  <textarea rows={4} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
                </label>
                <label className="manage-field manage-field--full">
                  <span>행사 정보</span>
                  <textarea rows={3} value={form.eventInfo} onChange={(e) => setForm({ ...form, eventInfo: e.target.value })} placeholder="일시 / 장소 / 대상" />
                </label>
                <label className="manage-field manage-field--full">
                  <span>비고</span>
                  <textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
                </label>
                <label className="manage-field">
                  <span>말씀 인용</span>
                  <input value={form.verseQuote} onChange={(e) => setForm({ ...form, verseQuote: e.target.value })} />
                </label>
                <label className="manage-field">
                  <span>말씀 출처</span>
                  <input value={form.verseRef} onChange={(e) => setForm({ ...form, verseRef: e.target.value })} placeholder="예: 마태복음 28:19" />
                </label>
              </div>
            </div>
            <div className="manage-modal-footer">
              <button type="button" className="manage-btn" onClick={closeForm}>취소</button>
              <button
                type="button"
                className="manage-btn manage-btn--primary"
                onClick={submitForm}
                disabled={saving || !form.title.trim() || !form.date}
              >
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
